<template>
  <div
    ref="list"
    :class="['ui-tabs-list', `ui-tabs-list--${tabs.variant.value}`]"
    role="tablist"
    :aria-label="label || undefined"
    @keydown="onKeydown"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue'
import { useTabs } from './context'

withDefaults(defineProps<{ label?: string }>(), { label: '' })

const tabs = useTabs('TabsList')
const list = useTemplateRef<HTMLElement>('list')

const STEPS: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (i, n) => (i + 1) % n,
  ArrowLeft: (i, n) => (i - 1 + n) % n,
  Home: () => 0,
  End: (_, n) => n - 1,
}

function onKeydown(event: KeyboardEvent) {
  const step = STEPS[event.key]
  if (!step || !list.value) return
  const triggers = Array.from(
    list.value.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'),
  )
  if (!triggers.length) return
  const from = triggers.indexOf(document.activeElement as HTMLButtonElement)
  const next = triggers[step(Math.max(from, 0), triggers.length)]
  if (!next) return
  event.preventDefault()
  next.focus()
  const value = next.dataset['value']
  if (value) tabs.select(value)
}
</script>

<style scoped>
.ui-tabs-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.ui-tabs-list--segmented {
  gap: 2px;
  width: fit-content;
  padding: 3px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface);
}

.ui-tabs-list--underline {
  gap: var(--space-5);
  border-bottom: 1px solid var(--color-border);
}
</style>
